import type { ReportData, GenerateReportResponse, SectionId } from '@lensora/shared'
import { ALL_SECTION_IDS } from '@lensora/shared'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FileText, FileDown, BarChart3, Users, Search, Target,
  Globe, Zap, List, Download, Calendar, Palette, Upload,
  Lock, History, TrendingUp, Star, ChevronDown, Link2,
  Activity, CheckSquare, Square, Loader2, X,
} from 'lucide-react'
import * as React from 'react'
import { HexColorPicker } from 'react-colorful'

import { useActiveWebsite } from '../../../hooks/use-active-website'
import { useTenantConfig } from '../../../hooks/use-tenant-config'
import { api, ApiError } from '../../../lib/api'
import { fetchBillingStatus } from '../../../lib/billing'

// ─── Types ─────────────────────────────────────────────────────────────────────

interface SavedReport {
  id: string
  created_at: string
  date_range_start: string
  date_range_end: string
  agency_name: string | null
  primary_colour: string
  sections: string[]
  report_data: ReportData
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const DATE_RANGES = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
]

const SECTION_OPTIONS: Array<{ id: SectionId; label: string; desc: string; Icon: React.ElementType }> = [
  { id: 'Executive Summary',      label: 'Executive Summary',      desc: 'AI-generated period overview',                  Icon: FileText },
  { id: 'Platform Performance',   label: 'Platform Performance',   desc: 'Breakdown by ChatGPT, Claude, Perplexity…',    Icon: BarChart3 },
  { id: 'Competitor Analysis',    label: 'Competitor Analysis',    desc: 'Leaderboard vs tracked competitors',           Icon: Users },
  { id: 'Top Performing Prompts', label: 'Top Performing Prompts', desc: 'Queries where your brand was mentioned',       Icon: Search },
  { id: 'Visibility Gaps',        label: 'Visibility Gaps',        desc: "Prompts where competitors appear, you don't",  Icon: Target },
  { id: 'Top Cited Sources',      label: 'Top Cited Sources',      desc: 'Domains Perplexity cites in your industry',   Icon: Globe },
  { id: 'Recommendations',        label: 'Recommendations',        desc: 'Top 5 actions from your Insights',            Icon: Zap },
  { id: 'Raw Simulation Data',    label: 'Raw Simulation Data',    desc: 'Full data export',                            Icon: List },
]

const DEFAULT_SECTIONS = SECTION_OPTIONS.filter((s) => s.id !== 'Raw Simulation Data').map((s) => s.id)

const PRESET_COLOURS = [
  '#ec4899', '#9B6BFF', '#20B8CD', '#4285F4',
  '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#000000', '#334155',
]

// Drives this page's own UI chrome (buttons, badges, active pills) — the
// tenant's actual brand tokens (TenantProvider sets these as CSS vars, see
// packages/ui/themes/default.css), not Lensora's hardcoded pink/purple.
// Unrelated to `primaryColour` below, which is the colour picked for the
// generated report PDF itself.
const REPORT_GRADIENT = 'linear-gradient(135deg, var(--brand-primary), var(--brand-accent))'

const MAX_LOGO_BYTES = 2 * 1024 * 1024
const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg']

// ─── Colour Picker ─────────────────────────────────────────────────────────────

function ColourPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = React.useState(false)
  const [hex, setHex] = React.useState(value)
  const [popoverPos, setPopoverPos] = React.useState({ top: 0, left: 0 })
  const swatchRef = React.useRef<HTMLButtonElement>(null)
  const popoverRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => { setHex(value) }, [value])

  React.useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (
        swatchRef.current && !swatchRef.current.contains(e.target as Node) &&
        popoverRef.current && !popoverRef.current.contains(e.target as Node)
      ) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  function commitHex(raw: string) {
    const v = raw.startsWith('#') ? raw : `#${raw}`
    setHex(v)
    if (/^#[0-9a-f]{6}$/i.test(v)) onChange(v)
  }

  function handleSwatchClick() {
    if (!open && swatchRef.current) {
      const rect = swatchRef.current.getBoundingClientRect()
      setPopoverPos({ top: rect.bottom + 8, left: rect.left })
    }
    setOpen((o) => !o)
  }

  return (
    <div className="inline-flex items-center gap-2">
      <button
        ref={swatchRef}
        type="button"
        onClick={handleSwatchClick}
        className={`h-8 w-8 shrink-0 rounded-lg transition-all ${open ? 'ring-2 ring-brand-accent ring-offset-1' : 'ring-1 ring-border'}`}
        style={{ background: value }}
      />
      <span className="font-mono text-xs text-text-muted">{value.toUpperCase()}</span>

      {open && (
        <div
          ref={popoverRef}
          className="fixed z-50 w-60 overflow-hidden rounded-xl border border-border bg-surface p-4 shadow-xl"
          style={{ top: popoverPos.top, left: popoverPos.left }}
        >
          <style>{`.react-colorful { width: 100% !important; }`}</style>
          <HexColorPicker
            color={value}
            onChange={(c) => { onChange(c); setHex(c) }}
            style={{ width: '100%', marginBottom: 12 }}
          />
          <input
            type="text"
            value={hex}
            onChange={(e) => setHex(e.target.value)}
            onBlur={() => commitHex(hex)}
            onKeyDown={(e) => { if (e.key === 'Enter') { commitHex(hex); setOpen(false) } }}
            maxLength={7}
            className="mb-3 w-full rounded-lg border border-border bg-surface-raised px-2.5 py-1.5 font-mono text-[13px] text-text outline-none focus:ring-2 focus:ring-brand-accent"
          />
          <div className="grid grid-cols-6 gap-1.5">
            {PRESET_COLOURS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => { onChange(c); setHex(c); setOpen(false) }}
                className="h-6 w-6 rounded-md transition-all hover:scale-110"
                style={{
                  background: c,
                  outline: value.toLowerCase() === c.toLowerCase() ? '2px solid #6366f1' : '1px solid #e2e8f0',
                  outlineOffset: 1,
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Count-Up Stat Card ─────────────────────────────────────────────────────────

function CountUpStat({
  label,
  target,
  icon,
  gradient,
  delay = 0,
}: {
  label: string
  target: number
  icon: React.ReactNode
  gradient: string
  delay?: number
}) {
  const [displayed, setDisplayed] = React.useState(0)
  const rafRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    if (target === 0) { setDisplayed(0); return }
    const duration = 900
    const start = performance.now() + delay
    function tick(now: number) {
      const elapsed = Math.max(0, now - start)
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplayed(Math.round(eased * target))
      if (progress < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [target, delay])

  return (
    <div className="flex items-center gap-3.5 rounded-xl border border-border bg-surface p-4">
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white"
        style={{ background: gradient }}
      >
        {icon}
      </div>
      <div>
        <p className="text-[22px] font-bold leading-none text-text">{displayed.toLocaleString()}</p>
        <p className="mt-1 text-[11px] text-text-muted">{label}</p>
      </div>
    </div>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export function ReportsPage() {
  const [agencyName, setAgencyName] = React.useState('')
  const [primaryColour, setPrimaryColour] = React.useState('#9B6BFF')
  const [logoBase64, setLogoBase64] = React.useState<string | null>(null)
  const [logoError, setLogoError] = React.useState<string | null>(null)
  const [dateRangeDays, setDateRangeDays] = React.useState(30)
  const [sections, setSections] = React.useState<SectionId[]>(DEFAULT_SECTIONS)
  const [generatedReport, setGeneratedReport] = React.useState<ReportData | null>(null)
  const [generatedReportId, setGeneratedReportId] = React.useState<string | null>(null)
  const [genError, setGenError] = React.useState<string | null>(null)
  const [exportError, setExportError] = React.useState<string | null>(null)
  const [sectionsOpen, setSectionsOpen] = React.useState(true)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()
  const colourInitialisedRef = React.useRef(false)

  // ── Queries ───────────────────────────────────────────────────────────────────

  const { data: tenantConfig } = useTenantConfig()

  // Pre-fill the report's primary colour with the tenant's own configured
  // brand colour (Settings → Brand) instead of defaulting every report to
  // Lensora's own purple — a client shouldn't have to notice and manually
  // re-pick a colour they've already set once. Runs once per page load, so a
  // deliberate manual pick isn't clobbered by a later config refetch.
  React.useEffect(() => {
    const brandColour = tenantConfig?.tenant.branding.brandAccent || tenantConfig?.tenant.branding.brandPrimary
    if (!brandColour || colourInitialisedRef.current) return
    colourInitialisedRef.current = true
    setPrimaryColour(brandColour)
  }, [tenantConfig])

  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['report-history'],
    queryFn: () => api.get<{ reports?: SavedReport[] }>('/api/reports/history'),
    staleTime: 30_000,
  })
  const reportHistory = historyData?.reports ?? []

  const { data: billingData, isLoading: billingLoading } = useQuery({
    queryKey: ['billing-status'],
    queryFn: fetchBillingStatus,
    staleTime: 300_000,
  })
  // planTier stays null (not laundered to 'starter') until the API confirms
  // it — a gate comparing against a specific tier string already fails
  // closed on null, but we still track "pending" separately so the UI can
  // show a neutral checking-state instead of a false "you're on Starter"
  // paywall while resolution is in flight.
  const planTier = billingData?.planTier ?? null
  const planPending = billingLoading || billingData?.planResolved === false
  const hasBranding = planTier === 'growth' || planTier === 'enterprise'

  const { websiteId } = useActiveWebsite()

  const { data: metricsData } = useQuery({
    queryKey: ['dashboard-metrics', websiteId],
    queryFn: () => api.get<{ total_scans?: number; prompts_tracked?: number }>(`/api/dashboard/metrics?website_id=${websiteId}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const { data: competitorsData } = useQuery({
    queryKey: ['competitors', websiteId],
    queryFn: () => api.get<{ competitors?: unknown[] }>(`/api/competitors?website_id=${websiteId}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const { data: sourcesData } = useQuery({
    queryKey: ['sources', websiteId],
    queryFn: () => api.get<{ domains?: Array<{ times_cited: number }> }>(`/api/sources?website_id=${websiteId}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const promptsTracked = metricsData?.total_scans ?? metricsData?.prompts_tracked ?? 0
  const competitorsMonitored = competitorsData?.competitors?.length ?? 0
  const citationsCollected = (sourcesData?.domains ?? []).reduce((acc, d) => acc + d.times_cited, 0)

  // ── Mutations ─────────────────────────────────────────────────────────────────

  const generateMutation = useMutation({
    mutationFn: () => {
      const end = new Date()
      const start = new Date(end.getTime() - dateRangeDays * 24 * 60 * 60 * 1000)
      return api.post<GenerateReportResponse>('/api/reports/generate', {
        date_range: { start: start.toISOString(), end: end.toISOString() },
        sections,
        agency_name: hasBranding ? (agencyName || null) : null,
        primary_colour: hasBranding ? primaryColour : '#9B6BFF',
        logo_base64: hasBranding ? logoBase64 : null,
      })
    },
    onSuccess: (report) => {
      setGeneratedReport(report)
      setGeneratedReportId(report.report_id)
      setGenError(null)
      queryClient.invalidateQueries({ queryKey: ['report-history'] })
    },
    onError: (err: unknown) => {
      setGenError(err instanceof ApiError ? err.message : 'Failed to generate report — please try again')
    },
  })

  const exportMutation = useMutation({
    mutationFn: async ({ reportId, history }: { reportId: string; history?: SavedReport }) => {
      const res = await api.postBlob(`/api/reports/${reportId}/pdf`, {
        agency_name: hasBranding ? (history?.agency_name ?? agencyName ?? null) : null,
        primary_colour: hasBranding ? (history?.primary_colour ?? primaryColour) : '#9B6BFF',
        logo_base64: hasBranding ? logoBase64 : null,
        sections: history ? toSectionIds(history.sections) : sections,
      })
      const blob = await res.blob()
      const disposition = res.headers.get('Content-Disposition')
      const match = disposition?.match(/filename="([^"]+)"/)
      const filename = match?.[1] ?? `report-${reportId}.pdf`
      return { blob, filename }
    },
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob)
      try {
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
      } finally {
        URL.revokeObjectURL(url)
      }
      setExportError(null)
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.code === 'RENDER_QUEUE_FULL') {
        setExportError('Report renderer is busy — try again in a few seconds')
      } else {
        setExportError(err instanceof ApiError ? err.message : 'Failed to generate PDF — please try again')
      }
    },
  })

  // ── Handlers ──────────────────────────────────────────────────────────────────

  function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      setLogoError('Logo must be a PNG or JPG file — SVG is not supported.')
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError('Logo must be 2MB or smaller.')
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (
        typeof result !== 'string' ||
        !(result.startsWith('data:image/png') || result.startsWith('data:image/jpeg'))
      ) {
        setLogoError('Could not read this file as a PNG or JPG image.')
        return
      }
      setLogoError(null)
      setLogoBase64(result)
    }
    reader.onerror = () => setLogoError('Could not read this file — please try again.')
    reader.readAsDataURL(file)
  }

  function handleClearLogo() {
    setLogoBase64(null)
    setLogoError(null)
  }

  function toggleSection(s: SectionId) {
    setSections((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s])
  }

  // Saved reports may carry section ids from before a section was renamed or
  // removed — filter against the live SectionId set so a stale id can't slip
  // through (and so the Contents-page count reflects sections that will
  // actually render).
  function toSectionIds(ids: string[]): SectionId[] {
    return ids.filter((id): id is SectionId => (ALL_SECTION_IDS as string[]).includes(id))
  }

  function handleExportPDF(reportId: string, history?: SavedReport) {
    setExportError(null)
    exportMutation.mutate(history ? { reportId, history } : { reportId })
  }

  return (
    <div className="mx-auto max-w-[860px] pb-24">
      {/* Header */}
      <div className="mb-7">
        <div className="mb-1.5 flex items-center gap-2.5">
          <FileDown className="h-5 w-5 text-brand-accent" />
          <h1 className="text-2xl font-bold text-text">Reports</h1>
        </div>
        <p className="text-sm text-text-muted">Export branded AI visibility reports for your clients</p>
      </div>

      {/* Two-column layout */}
      <div className="grid gap-5" style={{ gridTemplateColumns: '1fr 280px', alignItems: 'start' }}>

        {/* LEFT: Config */}
        <div className="flex flex-col gap-4">

          {/* ── Report Branding ── */}
          <div className="overflow-hidden rounded-2xl border border-border bg-surface">
            <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
              <Palette className="h-4 w-4 text-brand-accent" />
              <h2 className="flex-1 text-sm font-semibold text-text">Report Branding</h2>
              <span
                className="rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white"
                style={{ background: REPORT_GRADIENT }}
              >
                Growth
              </span>
            </div>

            <div className="relative p-5">
              <div
                className="flex flex-col gap-3"
                style={{
                  filter: hasBranding ? undefined : 'blur(3px)',
                  pointerEvents: hasBranding ? undefined : 'none',
                  userSelect: hasBranding ? undefined : 'none',
                }}
              >
                {/* Agency name + colour */}
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-text-muted">Agency Name</label>
                    <input
                      type="text"
                      value={agencyName}
                      onChange={(e) => setAgencyName(e.target.value)}
                      placeholder="Your agency name"
                      className="w-full rounded-lg border border-border bg-surface-raised px-3 py-2 text-[13px] text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-accent"
                    />
                  </div>
                  <div className="shrink-0">
                    <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-text-muted">Primary Colour</label>
                    <ColourPicker value={primaryColour} onChange={setPrimaryColour} />
                  </div>
                </div>

                {/* Logo upload */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2.5">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                        logoBase64
                          ? 'border-feedback-success/25 bg-feedback-success/10 text-feedback-success'
                          : 'border-border bg-surface-overlay text-text-muted hover:text-text'
                      }`}
                    >
                      <Upload className="h-3 w-3" />
                      {logoBase64 ? 'Logo uploaded ✓' : 'Upload logo'}
                    </button>
                    {logoBase64 && (
                      <button
                        type="button"
                        onClick={handleClearLogo}
                        className="flex shrink-0 items-center gap-1 rounded-lg border border-border bg-surface-overlay px-2.5 py-1.5 text-xs text-text-muted transition-colors hover:text-feedback-error"
                      >
                        <X className="h-3 w-3" /> Clear logo
                      </button>
                    )}
                    <span className="text-[11px] text-text-muted">
                      {logoBase64 ? 'Click to replace' : 'PNG or JPG · max 2MB · optional'}
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                  </div>
                  {logoError && (
                    <p className="text-[11px] text-feedback-error">{logoError}</p>
                  )}
                </div>
              </div>

              {/* Paywall overlay */}
              {!hasBranding && planPending && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin text-text-muted" />
                  <p className="text-sm text-text-muted">Checking your plan…</p>
                </div>
              )}
              {!hasBranding && !planPending && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-brand-accent/25 bg-brand-accent/10">
                    <Lock className="h-5 w-5 text-brand-accent" />
                  </div>
                  <p className="text-sm font-semibold text-text">White-label branding</p>
                  <p className="max-w-[220px] text-center text-xs text-text-muted">
                    Upgrade to Growth to unlock white-label branding
                  </p>
                  <button
                    className="mt-1 flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold text-white"
                    style={{ background: REPORT_GRADIENT }}
                  >
                    <Star className="h-3 w-3" /> Upgrade to Growth
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Date Range ── */}
          <div className="rounded-2xl border border-border bg-surface p-5">
            <div className="mb-3.5 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-brand-accent" />
              <label className="text-sm font-semibold text-text">Date Range</label>
            </div>
            <div className="flex gap-2">
              {DATE_RANGES.map((dr) => (
                <button
                  key={dr.days}
                  onClick={() => setDateRangeDays(dr.days)}
                  className={`rounded-lg px-4 py-2 text-xs font-medium transition-all ${
                    dateRangeDays === dr.days
                      ? 'text-white'
                      : 'border border-border bg-surface-overlay text-text-muted hover:text-text'
                  }`}
                  style={dateRangeDays === dr.days ? { background: REPORT_GRADIENT } : {}}
                >
                  {dr.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Sections to Include ── */}
          <div className="rounded-2xl border border-border bg-surface p-5">
            <div className="mb-3.5 flex items-center gap-2">
              <FileText className="h-4 w-4 text-brand-accent" />
              <label className="text-sm font-semibold text-text">Sections to Include</label>
            </div>

            <button
              onClick={() => setSectionsOpen((o) => !o)}
              className={`flex w-full items-center justify-between rounded-xl border px-3.5 py-2.5 text-left transition-colors ${
                sectionsOpen
                  ? 'border-brand-accent/30 bg-brand-accent/5'
                  : 'border-border bg-surface-raised'
              }`}
            >
              <span className="text-[13px] font-medium text-text">
                {sections.length === SECTION_OPTIONS.length
                  ? 'All sections selected'
                  : `${sections.length} section${sections.length !== 1 ? 's' : ''} selected`}
              </span>
              <ChevronDown
                className={`h-3.5 w-3.5 text-text-muted transition-transform ${sectionsOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {sectionsOpen && (
              <div className="mt-2 overflow-hidden rounded-xl border border-border bg-surface-raised">
                {SECTION_OPTIONS.map((s, i) => {
                  const checked = sections.includes(s.id)
                  return (
                    <button
                      key={s.id}
                      onClick={() => toggleSection(s.id)}
                      className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        i < SECTION_OPTIONS.length - 1 ? 'border-b border-border' : ''
                      } ${checked ? 'bg-brand-accent/5' : 'hover:bg-surface-overlay'}`}
                    >
                      {checked
                        ? <CheckSquare className="h-3.5 w-3.5 shrink-0 text-brand-accent" />
                        : <Square className="h-3.5 w-3.5 shrink-0 text-text-muted opacity-50" />
                      }
                      <div
                        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                          checked ? 'bg-brand-accent/12 text-brand-accent' : 'bg-surface-overlay text-text-muted'
                        }`}
                      >
                        <s.Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1">
                        <p className={`text-xs font-semibold ${checked ? 'text-text' : 'text-text-muted'}`}>{s.label}</p>
                        <p className="text-[10px] text-text-muted opacity-70">{s.desc}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* ── Error ── */}
          {genError && (
            <div className="rounded-xl border border-feedback-error/20 bg-feedback-error/5 px-4 py-3">
              <p className="text-[13px] text-feedback-error">{genError}</p>
            </div>
          )}

          {/* ── Generate button ── */}
          <button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending || sections.length === 0}
            className="mb-6 flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            style={{ background: REPORT_GRADIENT }}
          >
            {generateMutation.isPending ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Generating Report…</>
            ) : (
              <><FileDown className="h-4 w-4" /> Generate Report — 2 credits</>
            )}
          </button>
        </div>

        {/* RIGHT: Stats + History */}
        <div className="flex flex-col gap-4">

          {/* ── Your Report Data ── */}
          <div className="overflow-hidden rounded-2xl border border-border bg-surface">
            <div className="border-b border-border px-4 py-3.5">
              <p className="text-xs font-semibold text-text">Your Report Data</p>
              <p className="mt-0.5 text-[10px] text-text-muted">Live snapshot of what will be included</p>
            </div>
            <div className="flex flex-col gap-2.5 p-4">
              <CountUpStat
                label="Prompts Tracked"
                target={promptsTracked}
                icon={<Activity className="h-4.5 w-4.5" />}
                gradient={REPORT_GRADIENT}
                delay={0}
              />
              <CountUpStat
                label="Competitors Monitored"
                target={competitorsMonitored}
                icon={<Users className="h-4.5 w-4.5" />}
                gradient="linear-gradient(135deg, var(--brand-accent), #20B8CD)"
                delay={80}
              />
              <CountUpStat
                label="Citations Collected"
                target={citationsCollected}
                icon={<Link2 className="h-4.5 w-4.5" />}
                gradient="linear-gradient(135deg, #22c55e, #20B8CD)"
                delay={160}
              />
            </div>
          </div>

          {/* ── Generated report ready ── */}
          {generatedReport && (
            <div className="rounded-2xl border border-brand-accent/30 bg-surface p-4">
              <div className="mb-3 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-feedback-success" />
                <div>
                  <p className="text-xs font-semibold text-text">Report Ready</p>
                  <p className="text-[10px] text-text-muted">{generatedReport.website_name}</p>
                </div>
              </div>
              <button
                onClick={() => generatedReportId && handleExportPDF(generatedReportId)}
                disabled={exportMutation.isPending || !generatedReportId}
                className="flex w-full items-center justify-center gap-1.5 rounded-xl py-2.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ background: REPORT_GRADIENT }}
              >
                {exportMutation.isPending
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Rendering…</>
                  : <><Download className="h-3.5 w-3.5" /> Download PDF</>
                }
              </button>
              {exportError && (
                <p className="mt-2 text-[11px] text-feedback-error">{exportError}</p>
              )}
            </div>
          )}

          {/* ── Report History ── */}
          <div className="overflow-hidden rounded-2xl border border-border bg-surface">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3.5">
              <History className="h-4 w-4 text-brand-accent" />
              <p className="text-xs font-semibold text-text">Report History</p>
            </div>

            {historyLoading ? (
              <div className="flex flex-col gap-2 p-4">
                {[1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-xl bg-surface-overlay" />)}
              </div>
            ) : reportHistory.length === 0 ? (
              <div className="py-6 text-center">
                <FileDown className="mx-auto mb-2 h-7 w-7 text-text-muted opacity-25" />
                <p className="text-xs text-text-muted">No reports generated yet</p>
              </div>
            ) : (
              <div>
                {reportHistory.map((r, i) => {
                  const start = new Date(r.date_range_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                  const end = new Date(r.date_range_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  return (
                    <div
                      key={r.id}
                      className={`flex items-center gap-2.5 px-4 py-2.5 ${i < reportHistory.length - 1 ? 'border-b border-border' : ''}`}
                    >
                      <FileDown className="h-4 w-4 shrink-0 text-brand-accent" />
                      <div className="min-w-0 flex-1">
                        <p className="overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-semibold text-text">
                          {r.report_data?.website_name || 'Report'}
                        </p>
                        <p className="mt-0.5 text-[10px] text-text-muted">{start} — {end}</p>
                      </div>
                      <button
                        onClick={() => handleExportPDF(r.id, r)}
                        disabled={exportMutation.isPending}
                        className="flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                        style={{ background: REPORT_GRADIENT }}
                      >
                        {exportMutation.isPending
                          ? <Loader2 className="h-2.5 w-2.5 animate-spin" />
                          : <Download className="h-2.5 w-2.5" />
                        } PDF
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}
